<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :raw-data="removable ? statsMock : stats"
      :chart-value-keys="currentChartValueKeys"
      x-value="date"
      y-value-prefix="stats."
      :yaxis="[{ title: { text: currentChartTitle } }]"
      :fill="displayMode === 'fres' ? { type: ['gradient'], opacity: [0.25], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.55 } } : { type: ['solid'], opacity: [1]}"
      :annotations="{xaxis: xAxisAnnotations}"
    >
      <template v-slot:header>
        <v-card-title class="pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ currentChartTitle }}: {{ $t(`variables.${selectedVariable}.long`) }}
              </div>
            </v-col>
            <v-col
              v-if="removable"
              cols="auto"
            >
              <v-btn
                icon
                style="pointer-events: auto"
                @click="$emit('remove')"
              >
                <v-icon small>
                  fal fa-times
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              v-else
              cols="auto"
              class="text-right"
            >
              <div class="d-flex align-center">
                <visual-list-toggle
                  :value.sync="viewMode"
                  class="mr-3"
                />
                <v-btn-toggle
                  v-model="displayMode"
                  mandatory
                  dense
                  color="primary"
                  borderless
                >
                  <v-btn
                    small
                    value="loads"
                  >
                    CL/AL
                  </v-btn>
                  <v-btn
                    small
                    value="fres"
                  >
                    FRES
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-col>
          </v-row>
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-4">
        <v-row
          no-gutters
          align="center"
        >
          <v-col>
            <div class="subtitle-2 grey--text text--grey-100">
              {{ currentChartTitle }}: {{ $t(`variables.${selectedVariable}.long`) }}
            </div>
          </v-col>
          <v-col
            cols="auto"
            class="text-right"
          >
            <div class="d-flex align-center">
              <visual-list-toggle
                v-if="!removable"
                :value.sync="viewMode"
                class="mr-3"
              />
              <v-btn-toggle
                v-model="displayMode"
                mandatory
                dense
                color="primary"
                borderless
              >
                <v-btn
                  small
                  value="loads"
                >
                  CL/AL
                </v-btn>
                <v-btn
                  small
                  value="fres"
                >
                  FRES
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-col>
        </v-row>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="tableData"
      >
        <template v-slot:item.date="{ item }">
          {{ $dayjs(item.date).format('ll - ddd') }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'

export default {
  name: 'FreshnessChart',
  components: { ChartListViewTable, LineChart, VisualListToggle },
  props: {
    selectedVariable: {
      type: String,
      required: true
    },
    stats: {
      type: Array,
      required: true
    },
    currentDate: {
      type: Object,
      default: () => null
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayMode: 'loads',
      viewMode: 'chart'
    }
  },
  computed: {
    currentChartValueKeys() {
      if (this.displayMode === 'loads') {
        return [
          { key: this.selectedVariable + '_CL', type: 'line', label: this.$t('variables.chronicLoad') },
          { key: this.selectedVariable + '_AL', type: 'line', label: this.$t('variables.acuteLoad') }
        ]
      } else {
        return [
          { key: this.selectedVariable + '_FRES', type: 'area', label: this.$t('variables.freshness') }
        ]
      }
    },
    currentChartTitle() {
      return this.displayMode === 'loads' ? this.$t('table_headers.chronic_acute_load') : this.$t('table_headers.freshness')
    },
    xAxisAnnotations() {
      const currentDate = this.currentDate

      if (!currentDate) return []
      const annotations = []

      for (let i = 0; i < 29; i++) {
        const checkDate = currentDate.clone().subtract(i, 'day')
        if (checkDate.day() === 0) {
          annotations.push({
            x: checkDate.valueOf()
          })
        }
      }

      return annotations
    },
    statsMock() {
      return this.stats.map(item => {
        return {
          date: item.start_at,
          stats: {
            [this.selectedVariable + '_CL']: Math.round(Math.random() * 100),
            [this.selectedVariable + '_AL']: Math.round(Math.random() * 100),
            [this.selectedVariable + '_FRES']: Math.round(Math.random() * 100)
          }
        }
      })
    },
    tableData() {
      const data = this.removable ? this.statsMock : this.stats
      return data.map(item => {
        const stats = item.stats || {}
        if (this.displayMode === 'loads') {
          return {
            date: item.date,
            chronicLoad: stats[this.selectedVariable + '_CL'] || 0,
            acuteLoad: stats[this.selectedVariable + '_AL'] || 0
          }
        } else {
          return {
            date: item.date,
            freshness: stats[this.selectedVariable + '_FRES'] || 0
          }
        }
      })
    },
    tableHeaders() {
      const baseHeaders = [
        {
          text: this.$t('table_headers.date'),
          value: 'date',
          sortable: true,
          width: '30%'
        }
      ]

      if (this.displayMode === 'loads') {
        return [
          ...baseHeaders,
          {
            text: this.$t('table_headers.chronic_load'),
            value: 'chronicLoad',
            sortable: true,
            align: 'center'
          },
          {
            text: this.$t('table_headers.acute_load'),
            value: 'acuteLoad',
            sortable: true,
            align: 'center'
          }
        ]
      } else {
        return [
          ...baseHeaders,
          {
            text: this.$t('table_headers.freshness'),
            value: 'freshness',
            sortable: true,
            align: 'center'
          }
        ]
      }
    }
  }
}
</script>
